import React, { useState, useEffect } from 'react';

/**
 * TrackCard component for displaying individual track information
 * Handles hover effects, delete functionality, and responsive design
 */
const TrackCard = ({ 
  track, 
  index, 
  onDelete, 
  isTouchDevice = false,
  touchedTrackId,
  onTrackTouch,
  onPreview,
  isPreviewing = false,
  isLoadingPreview = false,
  previewError = null,
  allowDelete = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Check if text is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      const nameElement = document.getElementById(`track-name-${index}`);
      const artistElement = document.getElementById(`track-artist-${index}`);
      
      if (nameElement && artistElement) {
        const nameOverflow = nameElement.scrollWidth > nameElement.clientWidth;
        const artistOverflow = artistElement.scrollWidth > artistElement.clientWidth;
        setIsOverflowing(nameOverflow || artistOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [index, track.name, track.artist]);

  const trackKey = track?.id || track?.spotifyId || `${track?.name || ''}-${track?.artist || ''}`;

  const handleMouseOver = () => {
    if (!isTouchDevice && allowDelete) {
      setShowDeleteButton(true);
    }
  };

  const handleMouseOut = () => {
    if (!isTouchDevice && allowDelete) {
      setShowDeleteButton(false);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(track.id ?? trackKey);
  };

  const handleCardClick = () => {
    if (isTouchDevice) {
      onTrackTouch(track);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(track.id ?? trackKey);
  };

  const isTouched = touchedTrackId === trackKey;

  const styles = {
    cardContainer: {
      position: 'relative',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '1px solid #e5e7eb',
      transform: isTouched ? 'scale(1.02)' : 'scale(1)',
      boxShadow: isTouched ? '0 4px 16px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    trackImage: {
      width: '100%',
      height: '120px',
      objectFit: 'cover',
      borderRadius: '8px',
      marginBottom: '0.75rem'
    },
    trackName: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '0.25rem',
      lineHeight: '1.3',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    trackArtist: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      lineHeight: '1.3',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    deleteButton: {
      position: 'absolute',
      top: '0.5rem',
      right: '0.5rem',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold',
      opacity: showDeleteButton || isTouched ? 1 : 0,
      transition: 'opacity 0.2s ease',
      zIndex: 10
    },
    expandButton: {
      position: 'absolute',
      top: '0.5rem',
      right: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      width: '20px',
      height: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '10px',
      color: '#6b7280',
      opacity: isOverflowing ? 1 : 0,
      transition: 'opacity 0.2s ease',
      zIndex: 5
    },
    expandedContent: {
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '6px',
      border: '1px solid #e5e7eb'
    },
    expandedText: {
      fontSize: '0.8rem',
      color: '#374151',
      lineHeight: '1.4'
    },
    previewButton: {
      marginTop: '0.75rem',
      width: '100%',
      padding: '0.5rem 0.75rem',
      borderRadius: '8px',
      border: '1px solid #6366f1',
      backgroundColor: '#eef2ff',
      color: '#4338ca',
      fontWeight: 600,
      fontSize: '0.85rem',
      cursor: isLoadingPreview ? 'wait' : 'pointer',
      opacity: isLoadingPreview ? 0.7 : 1,
      transition: 'all 0.2s ease'
    },
    previewButtonActive: {
      backgroundColor: '#6366f1',
      color: '#ffffff',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
    },
    previewError: {
      marginTop: '0.5rem',
      fontSize: '0.75rem',
      color: '#ef4444',
      textAlign: 'center'
    }
  };

  return (
    <div
      style={styles.cardContainer}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleCardClick}
      onTouchStart={isTouchDevice ? (e) => e.stopPropagation() : undefined}
      className={isTouched ? 'touched' : ''}
    >
      {track.image_url && (
        <img
          src={track.image_url}
          alt={`${track.name} album cover`}
          style={styles.trackImage}
        />
      )}
      
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            id={`track-name-${index}`}
            style={styles.trackName}
          >
            {track.name}
          </div>
          
          <div
            id={`track-artist-${index}`}
            style={styles.trackArtist}
          >
            by {track.artist}
          </div>
        </div>
        {isOverflowing && (
          <button
            style={{
              ...styles.expandButton,
              position: 'relative',
              top: '0',
              right: '0',
              flexShrink: 0
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>

      {isExpanded && isOverflowing && (
        <div style={styles.expandedContent}>
          <div style={styles.expandedText}>
            <strong>Track:</strong> {track.name}
          </div>
          <div style={styles.expandedText}>
            <strong>Artist:</strong> {track.artist}
          </div>
        </div>
      )}

      {allowDelete && (
        <button
          style={styles.deleteButton}
          onClick={handleDeleteClick}
          onTouchStart={(e) => e.stopPropagation()}
          title="Delete track"
        >
          ×
        </button>
      )}

      <button
        type="button"
        style={{
          ...styles.previewButton,
          ...(isPreviewing && !isLoadingPreview ? styles.previewButtonActive : {})
        }}
        disabled={isLoadingPreview}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onPreview && onPreview(track);
        }}
      >
        {isLoadingPreview
          ? 'Loading Preview...'
          : isPreviewing
            ? 'Pause Preview'
            : 'Play Preview'}
      </button>

      {previewError && (
        <div style={styles.previewError}>{previewError}</div>
      )}
    </div>
  );
};

export default TrackCard;
